import { useState, useEffect } from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
    getFilteredRowModel,
    ColumnFiltersState,
} from "@tanstack/react-table";
import {
    Search,
    Loader2,
    Truck,
    Package as PackageIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator"; // Need Separator
import { Label } from "@/components/ui/label";

// I need Separator. I'll use <hr /> or create it.
// Assuming Separator exists in UI? I checked list_dir earlier, it wasn't there.
// I'll check package.json -> @radix-ui/react-separator is there.
// I'll create separator.tsx if needed, or just use div border.

// Types
type Order = {
    id: string;
    orderNumber: string;
    customer: {
        name: string;
        phone: string;
        address: string;
        city?: string;
    };
    total: number;
    status: string;
    createdAt: string;
    items: Array<{
        id: string;
        name: string;
        quantity: number;
        total: number; // Item total price
        product?: {
            images?: string[];
        }
    }>;
    courier?: {
        provider?: string;
        trackingId?: string;
    };
};

const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info"> = {
    pending: "warning",
    confirmed: "info",
    processing: "info",
    shipping: "secondary",
    delivered: "success",
    cancelled: "destructive",
    returned: "destructive",
};

export function OrdersPage() {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // Fetch Orders
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/admin/orders`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setOrders(data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch orders", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const handleRowClick = async (order: Order) => {
        // Fetch full details for the order
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/admin/orders/${order.id}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setSelectedOrder(data.data);
                setIsSheetOpen(true);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleStatusChange = async (newStatus: string) => {
        if (!selectedOrder) return;
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/admin/orders/${selectedOrder.id}/status`, {
                method: "PATCH",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ status: newStatus }),
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedOrder(data.data); // Update sheet
                fetchOrders(); // Refresh table
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleCourierUpdate = async (trackingId: string) => {
        if (!selectedOrder) return;
        try {
            const apiUrl = import.meta.env.VITE_API_URL || '';
            const res = await fetch(`${apiUrl}/admin/orders/${selectedOrder.id}/courier`, {
                method: "PATCH",
                credentials: 'include',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    provider: selectedOrder.courier?.provider || "Manual", // Default or current
                    trackingId
                }),
            });
            if (res.ok) {
                const data = await res.json();
                setSelectedOrder(data.data);
                fetchOrders();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const columns: ColumnDef<Order>[] = [
        {
            accessorKey: "orderNumber",
            header: "Order ID",
            cell: ({ row }) => <span className="font-mono">{row.getValue("orderNumber")}</span>,
        },
        {
            accessorKey: "customer.name",
            header: "Customer",
        },
        {
            accessorKey: "customer.phone",
            header: "Phone",
        },
        {
            accessorKey: "total",
            header: "Total",
            cell: ({ row }) => {
                const amount = parseFloat(row.getValue("total"));
                return <div className="font-medium">${amount.toFixed(2)}</div>; // Assuming USD or formatter
            },
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const status = row.getValue("status") as string;
                return (
                    <Badge variant={statusColors[status] || "outline"} className="capitalize">
                        {status}
                    </Badge>
                );
            },
        },
        {
            accessorKey: "createdAt",
            header: "Date",
            cell: ({ row }) => {
                return new Date(row.getValue("createdAt")).toLocaleDateString();
            }
        }
    ];

    const table = useReactTable({
        data: orders,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: {
            columnFilters,
        },
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
                    <p className="text-muted-foreground">Manage customer orders and shipments.</p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Recent Orders</CardTitle>
                        <div className="relative w-[300px]">
                            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search orders..."
                                value={(table.getColumn("customer.name")?.getFilterValue() as string) ?? ""}
                                onChange={(event) =>
                                    table.getColumn("customer.name")?.setFilterValue(event.target.value)
                                }
                                className="pl-8"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && "selected"}
                                            className="cursor-pointer"
                                            onClick={() => handleRowClick(row.original)}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {loading ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : "No orders found."}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-center justify-end space-x-2 py-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Next
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Order Details</SheetTitle>
                        <SheetDescription>
                            Order {selectedOrder?.orderNumber}
                        </SheetDescription>
                    </SheetHeader>

                    {selectedOrder && (
                        <div className="space-y-6 py-6">
                            {/* Status Updater */}
                            <div className="space-y-2">
                                <Label>Order Status</Label>
                                <Select
                                    value={selectedOrder.status}
                                    onValueChange={handleStatusChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="confirmed">Confirmed</SelectItem>
                                        <SelectItem value="processing">Processing</SelectItem>
                                        <SelectItem value="shipping">Shipping</SelectItem>
                                        <SelectItem value="delivered">Delivered</SelectItem>
                                        <SelectItem value="cancelled">Cancelled</SelectItem>
                                        <SelectItem value="returned">Returned</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Separator />

                            {/* Customer Info */}
                            <div>
                                <h3 className="font-semibold mb-2 flex items-center gap-2">
                                    Customer Information
                                </h3>
                                <div className="text-sm space-y-1 text-muted-foreground">
                                    <p className="text-foreground font-medium">{selectedOrder.customer.name}</p>
                                    <p>{selectedOrder.customer.phone}</p>
                                    <p>{selectedOrder.customer.address}</p>
                                    {selectedOrder.customer.city && <p>{selectedOrder.customer.city}</p>}
                                </div>
                            </div>

                            <Separator />

                            {/* Order Items */}
                            <div>
                                <h3 className="font-semibold mb-2">Items</h3>
                                <div className="space-y-4">
                                    {selectedOrder.items.map((item) => (
                                        <div key={item.id} className="flex items-start gap-4">
                                            {item.product?.images?.[0] ? (
                                                <img
                                                    src={item.product.images[0]}
                                                    alt={item.name}
                                                    className="w-12 h-12 rounded object-cover border"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded bg-muted flex items-center justify-center">
                                                    <PackageIcon className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <p className="text-sm font-medium">{item.name}</p>
                                                <div className="flex justify-between text-sm text-muted-foreground mt-1">
                                                    <span>Qty: {item.quantity}</span>
                                                    <span>${item.total.toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 pt-4 border-t space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Total</span>
                                        <span className="font-bold">${selectedOrder.total.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* Courier / Shipping */}
                            <div className="space-y-3">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Truck className="h-4 w-4" /> Shipping Details
                                </h3>
                                <div className="space-y-2">
                                    <Label>Tracking ID</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Enter tracking ID"
                                            defaultValue={selectedOrder.courier?.trackingId || ""}
                                            onBlur={(e) => {
                                                if (e.target.value !== selectedOrder.courier?.trackingId) {
                                                    handleCourierUpdate(e.target.value);
                                                }
                                            }}
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Enter tracking ID and click outside to save.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}


